import React from 'react';
import { debounce } from 'lodash';
import {
  getNameForUserUuid,
  createNewCoach,
  getCoachCreateFields,
} from 'coaching/actions/CoachingActions';
import { assertions } from 'lib/assertions';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import CheckboxList from 'components/CheckboxList';
import ErrorReporter from 'app/components/ErrorReporter';
import FormElement from 'components/FormElement';
import Input from 'components/Input';
import LoadingIndicator from 'components/LoadingIndicator';
import Row from 'components/Row';
import Text from 'components/Text';
import Divider from 'components/Divider';
import Icon from 'components/Icon';
import Select, { Option } from 'components/Select';

import ToastActions from 'actions/ToastActions';

export type Props = {
  onHide: () => void;
  nameQuery: string;
};

type State = {
  coachUuid?: string;
  coachName?: string;
  loadingCoachName: boolean;
  selectedSubjects: string[];
  submittingNewCoach: boolean;
  hourlyRateInCentsSelected?: number | null;
  hourlyRateInCents?: number[] | undefined;
  subjects: string[] | undefined;
  error?: string | undefined | null;
  loading: boolean;
};

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export default class CoachingCoordinatorAddCoachSidebar extends React.Component<Props, State> {
  state: State = {
    submittingNewCoach: false,
    loadingCoachName: false,
    selectedSubjects: [],
    subjects: [],
    hourlyRateInCentsSelected: null,
    hourlyRateInCents: undefined,
    error: null,
    loading: true,
    coachUuid: undefined,
  };

  async componentDidMount() {
    const { subjects = [], hourlyRateInCents = [], error } = await getCoachCreateFields();
    if (error) {
      ToastActions.showErrorToast(error);
    }
    this.setState({ error, loading: false, subjects, hourlyRateInCents });
  }

  handleAddCoach = () => {
    const { coachUuid, hourlyRateInCentsSelected } = this.state;
    if (!coachUuid) {
      console.error(
        'CoachingCoordinatorAddCoachSidebar - tried to set coach name without coachUuid'
      );
      return;
    }
    if (!hourlyRateInCentsSelected) {
      console.error(
        'CoachingCoordinatorAddCoachSidebar - tried to set coach name without hourly rate'
      );
      return;
    }

    this.setState({ submittingNewCoach: true });
    return createNewCoach(
      coachUuid,
      this.state.selectedSubjects,
      hourlyRateInCentsSelected,
      this.props.nameQuery
    ).then(this.resetCoachState);
  };

  resetCoachState = () => {
    this.props.onHide();
    this.setState({
      loadingCoachName: false,
      selectedSubjects: [],
      coachName: undefined,
      coachUuid: undefined,
      submittingNewCoach: false,
      hourlyRateInCentsSelected: null,
    });
  };

  setCoachUuidAndName = (coachUuid: string) => {
    this.setState({ coachUuid }, this.fetchAndSetCoachName);
  };

  fetchAndSetCoachName = debounce(() => {
    const { coachUuid } = this.state;
    if (!coachUuid) {
      console.error(
        'CoachingCoordinatorAddCoachSidebar - tried to set coach name without coachUuid'
      );
      return;
    }

    this.setState({ loadingCoachName: true });
    return getNameForUserUuid(coachUuid).then(name =>
      this.setState({ coachName: name, loadingCoachName: false })
    );
  }, 500);

  renderCreateCoachFields = () => {
    const { error } = this.state;
    const { subjects, hourlyRateInCents } = this.state;

    if (error || !subjects || !hourlyRateInCents) {
      return (
        <Row>
          <ErrorReporter error="Could not fetch coachable subjects" />
        </Row>
      );
    }
    const subjectOptions = new Map<string, string>(subjects.map(subject => [subject, subject]));
    const hourlyRateOptions = hourlyRateInCents.map((hourlyRateInCentsOption: number) => ({
      label: currencyFormatter.format(hourlyRateInCentsOption / 100),
      value: hourlyRateInCentsOption,
    }));
    return (
      <>
        <Row>
          <CheckboxList
            data-test="subject-checkbox-list"
            options={subjectOptions}
            onChange={(selectedSubjects: string[]) => this.setState({ selectedSubjects })}
            checked={this.state.selectedSubjects}
          />
        </Row>
        <Row>
          <Select
            data-test="subject-hourly-rate-select"
            options={hourlyRateOptions}
            onChange={(hourlyRateInCents: string) =>
              this.setState({ hourlyRateInCentsSelected: parseInt(hourlyRateInCents) })
            }
            placeholder="Hourly Rate"
            value={this.state.hourlyRateInCentsSelected}
          />
        </Row>
      </>
    );
  };

  render() {
    const {
      loadingCoachName,
      coachName,
      hourlyRateInCentsSelected,
      coachUuid,
      submittingNewCoach,
      loading,
    } = this.state;
    const { onHide } = this.props;
    const inputSubtext = (loadingCoachName || coachName) && (
      <Text size="xxsmall" color="graphite" data-test="coach-name-text">
        {loadingCoachName ? <LoadingIndicator size="xsmall" /> : `Name: ${coachName}`}
      </Text>
    );

    if (loading) {
      return <CenteredLoadingIndicator />;
    }

    return (
      <BoxBody data-test="add-coach-container" size="small">
        <Text align="right">
          <Button level="icon" color="chalkboard" onClick={onHide}>
            <Icon name="x" />
          </Button>
        </Text>
        <Divider size="xxsmall" />
        <Row size="large">
          <Text size="large">Create a new coach</Text>
        </Row>
        <Row size="small">
          <FormElement
            data-test="coach-uuid-input"
            element={Input}
            validate={e => assertions.uuid(e.target.value)}
            label="Search by UUID"
            onValidChange={e => this.setCoachUuidAndName(e.target.value)}
            subtext={inputSubtext}
            elementProps={{ value: coachUuid, placeholder: "Paste the coach's user UUID" }}
          />
        </Row>
        {this.renderCreateCoachFields()}
        <Button
          data-test="add-new-coach-button"
          loading={submittingNewCoach}
          disabled={!coachName && !coachUuid && !hourlyRateInCentsSelected}
          onClick={this.handleAddCoach}>
          Add coach
        </Button>
      </BoxBody>
    );
  }
}
